import React, { FC, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import {
  ButtonClean,
  ButtonPrimary,
  FullBlockSpinner,
  Modal
} from 'front-end/src/components'
import ErrorMessage from '../../../components/common/error/ErrorMessage'
import Form from '../../../components/common/form/Form'
import Input from '../../../components/common/form/input/Input'
import { TargetUserIds } from '../ManageUsersPageContext'
import { PartialGroup } from '../types/IGroup'
import { SelectEntitiesForm } from 'front-end/src/components/common/selectEntitiesForm'
import { SearchableItemTypesEnum } from 'shared/enums/common'
import { useGroupSearchQuery } from './AddUsersToGroupsModal.codegen'
import { useBatchAddUsersToGroupsMutation } from '../usersAddToGroupBatch.codegen'

export type AddUsersToGroupsModalPropsType = {
  target: TargetUserIds
  groups: PartialGroup[]
  show: boolean
  excludeExistingGroups?: boolean
  onHide: () => void
  onCompleted?: () => void
}

export type AddGroupModalStateType = {
  error?: Error
  groupName: string
  groupId?: number
  loading: boolean
}

const getDefaultState = (): AddGroupModalStateType => ({
  error: undefined,
  groupName: '',
  groupId: undefined,
  loading: false
})

export const AddUsersToGroupsModal: FC<AddUsersToGroupsModalPropsType> = ({
  ...props
}) => {
  const [state, setState] = useState<AddGroupModalStateType>(getDefaultState())
  const [selectedIds, setSelectedIds] = useState([] as number[])
  const changeState = (newStateProps: Partial<AddGroupModalStateType>) => {
    setState({ ...state, ...newStateProps })
  }
  const { target: userIds, onHide, onCompleted } = props
  const { error, groupName } = state
  const [batchAddUsersToGroupsMutation] = useBatchAddUsersToGroupsMutation({
    variables: {
      userIds,
      groupName,
      groupIds: selectedIds
    },
    onCompleted: () => {
      onCompleted && onCompleted()
    }
  })

  const onNameChange = (groupName) => {
    changeState({ groupName })
    setSelectedIds([])
  }

  const onSubmit = async () => {
    changeState({ loading: true })

    try {
      await batchAddUsersToGroupsMutation()
      onHide()
    } catch (error) {
      changeState({ error })
    } finally {
      changeState({ loading: false })
    }
  }

  const reset = () => changeState(getDefaultState())

  const renderControls = () => {
    const { onHide } = props
    const { loading } = state

    return [
      <ButtonClean key={1} onClick={onHide}>
        <Trans>Cancel</Trans>
      </ButtonClean>,
      <ButtonPrimary key={2} onClick={onSubmit} disabled={loading}>
        <Trans>Add to group(s)</Trans>
      </ButtonPrimary>
    ]
  }

  const { show } = props

  const { data, loading } = useGroupSearchQuery()

  if (loading) {
    return <FullBlockSpinner />
  }

  const {
    admin: { searchGroupsByName: groups }
  } = data

  return (
    <Modal
      className="AddGroupModal"
      header={t`Add to group(s)`}
      onHide={onHide}
      show={show}
      onHidden={reset}
      controls={renderControls()}
    >
      <Form onSubmit={onSubmit}>
        {error && <ErrorMessage message={t`This group can't be added`} />}
        <SelectEntitiesForm
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          entityType={SearchableItemTypesEnum.groups}
          entities={groups}
        />
        <p>
          <br />
          <Trans>or create a new group:</Trans>
        </p>
        <Input
          fullWidth={true}
          onChange={onNameChange}
          value={groupName || ''}
        />
      </Form>
    </Modal>
  )
}
